import { css } from '@emotion/react';
import { Typography, Link } from '@mui/material';
import { Fragment, useLayoutEffect } from 'react';
import { useHistory, Link as RouterLink } from 'react-router-dom';

import { shouldBePassword } from '@jebel/utils';

import { useAppAuth } from 'providers/useAppAuth';
import { PasswordField } from 'shared/components/form';
import { DividerOr } from 'shared/components/symbols';
import { Box, Button, Form, FormTextField, RouteLayout } from 'shared/components/ui';
import { APP_URL, ROUTES } from 'shared/constants';
import { useQueryParams, useToast } from 'shared/hooks';
import { isValidEmail } from 'shared/utils/form';

import { SocialButtons } from '../components/SocialButtons';

const formWrapperCss = css`
  display: grid;
  grid-row-gap: 20px;
`;

const formTitleCss = css`
  font-size: 26px;
  font-weight: 500;
  width: 100%;
  text-align: center;
`;

const formBlock = css`
  display: grid;
  gap: 1rem;
`;

const bottomLinkWrap = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

type FormData = {
  email?: string;
  password?: string;
};

export function AuthPage() {
  const [params] = useQueryParams();

  const { hasSession, login, confirmSocialSignIn } = useAppAuth();
  const { push: navigate } = useHistory();
  const { showError } = useToast();

  useLayoutEffect(() => {
    if (params.code) {
      confirmSocialSignIn(params.code);
    }
  }, [params.code]);

  useLayoutEffect(() => {
    if (hasSession) {
      redirect();
    }
  }, [hasSession]);

  const redirect = () => {
    navigate(ROUTES.user.home.index);
  };

  const onSubmit = async ({ email, password }: FormData) => {
    if (!email || !password) {
      return;
    }

    try {
      await login({ email, password });
      redirect();
    } catch (err) {
      showError('Something goes wrong when validating your credentials, please try again.');

      if (err instanceof Error) {
        showError(err.message);
        return;
      }
    }
  };

  return (
    <RouteLayout>
      <Box css={formWrapperCss}>
        <Typography
          css={formTitleCss}
          variant="inherit"
          align="inherit"
          color="initial"
          paragraph={false}
        >
          Welcome! Login here
        </Typography>

        <Form oldCss={formBlock} onSubmit={onSubmit} validateOnBlur={false}>
          {({ isSubmitting }) => (
            <Fragment>
              <FormTextField
                inputProps={{
                  color: 'primary',
                  label: 'Email Address',
                  variant: 'outlined',
                }}
                fieldProps={{ name: 'email', validate: isValidEmail }}
              />

              <PasswordField
                name="password"
                label="Enter your password"
                validate={shouldBePassword}
                autoComplete="password"
                required
              />

              <Button
                loading={isSubmitting}
                variant="contained"
                size="large"
                type="submit"
                color="secondary"
              >
                Login
              </Button>

              <DividerOr />

              <SocialButtons />

              <Box css={bottomLinkWrap}>
                <Typography variant="inherit" align="inherit" color="initial" paragraph={false}>
                  Forgot your password?&nbsp;
                </Typography>

                <Link component={RouterLink} to={APP_URL.public.recovery}>
                  Click here
                </Link>
              </Box>

              <Box css={bottomLinkWrap}>
                <Typography variant="inherit" align="inherit" color="initial" paragraph={false}>
                  No account?&nbsp;
                </Typography>

                <Link component={RouterLink} to={APP_URL.public.request}>
                  Request access
                </Link>
              </Box>
            </Fragment>
          )}
        </Form>
      </Box>
    </RouteLayout>
  );
}
